import { Component, OnDestroy, NgZone, ViewChild, ViewChildren, QueryList, ElementRef, AfterViewInit, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { ConnectionService } from '@services/connection-service';
import { LocalStorageService } from '@services/local-storage-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService, OperatingSystem } from '@services/app-web-bridge-service';
import { EventLoggerService } from '@services/event-logger-service';
import { LoginService } from '@services/login-service/login.service';
import { WindowRefService } from '@services/window-ref-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { CookieService } from '@services/cookie-service';
import { ExperimentService } from '@services/experiment';
import { ShiprocketLoginService } from '@services/shiprocket-login';
import { BotProtectionService } from '@services/bot-protection-service';
import { AppConfig } from '../../app.config';
import { AuthConfig } from '../../app-constant-types';

const SHIPROCKET_LOGIN_METHOD = 'shiprocket';

@Component({
  selector: 'onboarding-otp',
  templateUrl: './onboarding-mobile-otp.html',
  standalone: false,
})

export class OnboardingMobileOtpComponent implements OnDestroy, AfterViewInit, OnInit {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly zone: NgZone = inject(NgZone);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  public readonly appConfig: AppConfig = inject(AppConfig);
  private readonly loginService: LoginService = inject(LoginService);
  private readonly windowRefService: WindowRefService = inject(WindowRefService);
  public readonly commonUtilityHelper: CommonUtilityHelper = inject(CommonUtilityHelper);
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly cookies: CookieService = inject(CookieService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly shiprocketService: ShiprocketLoginService = inject(ShiprocketLoginService);
  private readonly botProtection: BotProtectionService = inject(BotProtectionService);
  private readonly router: Router = inject(Router);
  mobileNumber: string;
  verificationCode: string;
  retryInterval: any;
  retryOTP: boolean = true;
  language: string;
  subscriptions: Array<Subscription> = [];
  startTimer: number;
  ui: { loading: boolean, error?: string, timeLefForOTPCall?: number } = { loading: false };
  otpError: boolean = false;
  @ViewChild('otpInput', { static: false }) otpInput: ElementRef;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;

  loginWithShiprocket: boolean = false;
  switchingToCureskinOtp: boolean = false;
  switchedFromShiprocket: boolean = false;
  isiOSDevice: boolean = false;
  readonly shiprocketOtpLength: number = 6;
  readonly shiprocketBoxes: number[] = [0, 1, 2, 3, 4, 5];
  shiprocketDigits: string[] = new Array(6).fill('');
  @ViewChildren('otpBox') otpBoxes: QueryList<ElementRef<HTMLInputElement>>;

  get shiprocketOtp(): string {
    return this.shiprocketDigits.join('');
  }

  get otpLength(): number {
    return this.loginWithShiprocket ? this.shiprocketOtpLength : 4;
  }

  get isOtpValid(): boolean {
    if (this.loginWithShiprocket) {
      return this.commonUtilityHelper.isValidNumberInput(this.shiprocketOtp, this.shiprocketOtpLength);
    }
    return this.commonUtilityHelper.isValidNumberInput(this.verificationCode, 4);
  }

  constructor() {
    this.mobileNumber = this.storage.getValue('CureSkin/mobileNumber') || '';
    this.language = this.storage.getValue('CureSkin/language') || '';
    this.loginWithShiprocket = !!this.shiprocketService.token;
  }

  ngOnInit(): void {
    this.startTimer = new Date().getTime();
    this.appWebBridge.requestOSInformation().then((os: string): void => {
      this.isiOSDevice = os === OperatingSystem.IOS || this.windowRefService.isSafariBrowser();
    });
    this.subscriptions.push(this.broadcast.on<string>('OTP_RECEIVED')
      .subscribe((otp: string): void => this.zone.run((): void => this.validateOtp(`${otp}`.substring(0, this.otpLength)))));
    this.startResendOtpTimer();
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'onboarding-OTP-verification' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    const time = new Date().getTime() - this.startTimer;
    this.eventLogger.trackEvent('otp_screen_loading', { timeInMillSec: time });
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'pageOpenOtp' },
      firebase: { name: 'pageOpenOtp' },
    });
  }

  ngAfterViewInit(): void {
    this.checkForOtpInSafariBrowser();
  }

  checkForOtpInSafariBrowser(): any {
    if (!(this.windowRefService.isSafariBrowser())) return 0;
    this.subscriptions.push(interval(1000)
      .subscribe((): void => this.validateOtp(this.otpInput?.nativeElement.value)));
    return 0;
  }

  startResendOtpTimer(): void {
    this.retryOTP = true;
    this.clearRetryInterval();
    this.ui.timeLefForOTPCall = 30;
    this.retryInterval = setInterval((): void => this.zone.run((): void => {
      this.ui.timeLefForOTPCall -= 1;
      if (this.ui.timeLefForOTPCall === 0) {
        this.clearRetryInterval();
        if (!this.loginWithShiprocket) this.sendOTPOnCall();
      }
    }), 1000);
  }

  onOtpClick(event: MouseEvent): void {
    const input = this.otpInput?.nativeElement;
    if (!input) return;
    const len = input.value?.length || 0;
    if (len === 0) return;
    // Each box is 48px wide + 8px gap = 56px stride
    const boxIndex = Math.min(Math.floor(event.offsetX / 56), 3);
    const cursorPos = Math.min(boxIndex + 1, len);
    setTimeout(() => input.setSelectionRange(cursorPos, cursorPos), 0);
  }

  onOtpChange(): void {
    this.verificationCode = this.verificationCode?.replace(/[^0-9]/g, '');
    this.otpError = false;
  }

  validateOtp(otp: string): void {
    this.zone.run((): void => {
      if (this.loginWithShiprocket) {
        if (!otp || otp.length !== this.shiprocketOtpLength) return;
        this.setShiprocketOtp(otp);
        this.verifyOtp();
        return;
      }
      if (!otp || otp.length !== 4) return;
      this.verificationCode = otp;
      this.verifyOtp();
    });
  }

  clearRetryInterval(): void {
    clearInterval(this.retryInterval);
    delete this.retryInterval;
  }

  async sendOTPOnCall(): Promise<void> {
    if (!this.retryOTP) return;
    this.eventLogger.trackEvent('otp_retry', {});
    // The server's OTP-retry path only verifies Turnstile, so reCAPTCHA is not requested here.
    const challengeParams = await this.botProtection.getOtpChallengeParams('retryOTP', false);
    this.conn.reSendOTP(this.storage.getValue('CureSkin/mobileNumber'), challengeParams)
      .then((): boolean => this.ui.loading = false)
      .catch((): void => {
        this.ui.loading = false;
        this.clearRetryInterval();
      });
  }

  async verifyOtp(): Promise<any> {
    if (this.ui.loading) return;
    if (this.loginWithShiprocket) {
      await this.verifyShiprocketOtp();
      return;
    }
    if (!this.isOtpValid) {
      this.otpError = true;
      this.broadcast.broadcast('NOTIFY', { message: $localize`:@@enterValidVerificationCode:Enter a valid ${this.otpLength}:otpLength: digit verification code.` });
      return;
    }
    this.ui.loading = true;
    this.eventLogger.trackEvent('otp_requested_time');
    const otp = this.verificationCode;
    const mobileNumber = this.storage.getValue('CureSkin/mobileNumber');
    const authConfig: AuthConfig = { mobileNumber, OTP: otp };
    try {
      await this.loginService.authenticateUser(authConfig, { enableOTPVerifiedLog: true });
      const time = new Date().getTime() - this.startTimer;
      this.eventLogger.trackEvent('otp_verification_page_time_spent', { timeInMilliSec: time });
      this.eventLogger.cleverTapEvent('loginSuccess', JSON.stringify({ loginMethod: 'otp' }));
    } catch (error) {
      this.otpError = true;
      this.broadcast.broadcast('NOTIFY', { message: 'Please enter a correct OTP' });
      this.eventLogger.trackEvent('otp_failure', { error: error.toString() });
    } finally {
      this.retryOTP = false;
      this.clearRetryInterval();
      this.ui.loading = false;
      this.unsubscribe();
    }
  }

  private async verifyShiprocketOtp(): Promise<void> {
    if (!this.isOtpValid) {
      this.otpError = true;
      this.broadcast.broadcast('NOTIFY', { message: $localize`:@@enterValidVerificationCode:Enter a valid ${this.shiprocketOtpLength}:otpLength: digit verification code.` });
      return;
    }
    this.ui.loading = true;
    this.eventLogger.trackEvent('otp_requested_time');
    const mobileNumber = `${this.storage.getValue('CureSkin/mobileNumber')}`;
    const deviceId = this.cookies.get('deviceId') || this.conn.uniqueId;
    const installationId = this.cookies.get('installationId') || deviceId;
    const source = await this.appWebBridge.requestOSInformation();
    const aaid = await this.appWebBridge.requestAndroidAdvertisingID();
    try {
      const { sessionToken } = await this.shiprocketService.verify({
        mobileNumber,
        token: this.shiprocketService.token!,
        OTP: this.shiprocketOtp,
        installationId,
        source,
        deviceId,
        aaid,
        userAddressConsent: this.shiprocketService.userAddressConsent,
      });
      this.eventLogger.trackEvent('otp_verified', { device_id: deviceId, mobileNumber });
      await this.conn.loginWithTokenAndUpdateUserInfo(sessionToken, { loginSource: source });
      await this.conn.checkUserLanguageAndRedirect(this.storage.getValue('CureSkin/redirectUrl') || '/');
      await this.experimentService.fetchActiveUserExperiment();
      if (source !== OperatingSystem.IOS) this.appWebBridge.requestNotificationPermission();
      this.eventLogger.trackEvent('shiprocket_verified', { device_id: deviceId, mobileNumber });
      const time = new Date().getTime() - this.startTimer;
      this.eventLogger.trackEvent('otp_verification_page_time_spent', { timeInMilliSec: time });
      this.eventLogger.cleverTapEvent('loginSuccess', JSON.stringify({ loginMethod: SHIPROCKET_LOGIN_METHOD }));
      this.retryOTP = false;
      this.clearRetryInterval();
      this.unsubscribe();
    } catch (error) {
      this.otpError = true;
      this.broadcast.broadcast('NOTIFY', { message: $localize`:@@pleaseEnterCorrectOtp:Please enter a correct OTP` });
      this.eventLogger.trackEvent('otp_failure', { error: error.toString() });
    } finally {
      this.ui.loading = false;
    }
  }

  // See login-service/CLAUDE.md — Shiprocket has no resend; this falls back to CureSkin OTP.
  async resendOtp(): Promise<void> {
    if (this.ui.timeLefForOTPCall > 0 || this.ui.loading) return;
    this.ui.loading = true;
    this.switchingToCureskinOtp = true;
    const mobileNumber = `${this.storage.getValue('CureSkin/mobileNumber')}`;
    const deviceId = this.cookies.get('deviceId') || this.conn.uniqueId;
    const source = await this.appWebBridge.requestOSInformation();
    this.eventLogger.trackEvent('otp_retry', { loginMethod: SHIPROCKET_LOGIN_METHOD, switchedToLoginMethod: 'otp' });
    this.eventLogger.trackEvent('otp_requested', { device_id: deviceId, mobileNumber });
    this.eventLogger.cleverTapEvent('otpRequested', JSON.stringify({ loginMethod: 'otp' }));
    this.appWebBridge.requestStartSMSListener();
    try {
      const challengeParams = await this.botProtection.getOtpChallengeParams('requestOTP');
      await this.conn.requestOTP(mobileNumber, source, challengeParams);
      this.switchToCureskinOtpFlow();
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: error instanceof Error ? error.message : $localize`:@@somethingWentWrongTryAgain:Something went wrong. Please try again.` });
    } finally {
      this.switchingToCureskinOtp = false;
      this.ui.loading = false;
    }
  }

  private switchToCureskinOtpFlow(): void {
    this.clearShiprocketOtp();
    this.shiprocketService.token = null;
    this.loginWithShiprocket = false;
    this.switchedFromShiprocket = true;
    this.verificationCode = '';
    this.startResendOtpTimer();
    setTimeout((): void => this.otpInput?.nativeElement.focus(), 0);
  }

  onShiprocketOtpInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/[^0-9]/g, '').slice(-1);
    this.shiprocketDigits[index] = digit;
    input.value = digit;
    this.otpError = false;
    if (digit && index < this.shiprocketOtpLength - 1) this.focusShiprocketBox(index + 1);
  }

  onShiprocketOtpKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.shiprocketDigits[index] && index > 0) this.focusShiprocketBox(index - 1);
  }

  onShiprocketOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/[^0-9]/g, '').slice(0, this.shiprocketOtpLength);
    if (!pasted) return;
    this.setShiprocketOtp(pasted);
    this.focusShiprocketBox(Math.min(pasted.length, this.shiprocketOtpLength - 1));
  }

  private setShiprocketOtp(otp: string): void {
    this.otpError = false;
    for (let i = 0; i < this.shiprocketOtpLength; i += 1) {
      this.shiprocketDigits[i] = otp[i] || '';
      const box = this.otpBoxes?.get(i)?.nativeElement;
      if (box) box.value = this.shiprocketDigits[i];
    }
  }

  private clearShiprocketOtp(): void {
    this.shiprocketDigits = new Array(this.shiprocketOtpLength).fill('');
    this.otpError = false;
    this.otpBoxes?.forEach((box: ElementRef<HTMLInputElement>): string => box.nativeElement.value = '');
  }

  private focusShiprocketBox(index: number): void {
    setTimeout((): void => this.otpBoxes?.get(index)?.nativeElement.focus(), 0);
  }

  editNumber(): void {
    this.eventLogger.trackEvent('otp_number_change', {});
    this.router.navigate(['/onboarding/number']);
  }

  /** Keeps the input and CTA visible when the on-screen keyboard shrinks the viewport. */
  onInputFocus(): void {
    setTimeout((): void => {
      if (!this.scrollContainer) return;
      const container = this.scrollContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }, 300);
  }

  navigateTo(url: string): void {
    this.conn.navigateToURL(url);
  }

  /** Opens an external URL outside the in-app iframe (Chrome Custom Tab on Android,
   * new browser tab on iOS/web). Used for Shiprocket's T&C / Privacy pages, which
   * block iframe embedding via X-Frame-Options / CSP frame-ancestors. */
  openExternalUrl(url: string): void {
    if (this.isiOSDevice) this.conn.navigateToURL(url);
    else this.broadcast.broadcast('OPEN_IN_NEW_TAB', { url });
  }

  ngOnDestroy(): void {
    this.clearRetryInterval();
    this.unsubscribe();
  }

  public unsubscribe(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
